import type { CSSProperties } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Checkbox, Empty, Input, InputNumber, Popconfirm, Segmented, Select, Space, Spin, Switch, Tag, Typography, message, notification } from 'antd'
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { crmIntegrationApi, type CrmIntegrationSettings as Settings, type CrmLeadSource, type PullResult, type PullStatusGroup, type SourceTemperature } from '../services/crmIntegrationApi'
import { leadsApi, type GeoFolder } from '../services/leadsApi'
import { temperatureColor } from '../constants/leadTemperature'

// На жёлтом тёплом белый текст не читается, поэтому подпись там тёмная.
const POOL_TEXT_COLORS: Record<string, string> = { cold: '#fff', warm: '#1a2332', hot: '#fff' }

// Подписи пулов TG-аккаунтов: те же три температуры, что и у аккаунта.
const TEMPERATURE_OPTIONS: Array<{ value: SourceTemperature; label: string }> = [
    { value: 'cold', label: 'Холодный' },
    { value: 'warm', label: 'Тёплый' },
    { value: 'hot', label: 'Горячий' },
]

const { Text } = Typography

function extractError(error: unknown): string {
    const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
    if (typeof detail === 'string') return detail
    return 'Не удалось выполнить действие'
}

const formatMoment = (value: string | null) => (value ? dayjs(value).format('DD.MM.YYYY HH:mm') : 'ещё не выполнялось')


// Тумблер со спросом. Подтверждение показывается вплотную к самому тумблеру,
// чтобы не искать окно глазами, а сам тумблер не переключается, пока не ответят.
function ConfirmSwitch({ checked, what, onConfirm, disabled }: {
    checked?: boolean
    what: string
    onConfirm: (next: boolean) => void
    disabled?: boolean
}) {
    const [open, setOpen] = useState(false)
    const next = !checked

    return (
        <Popconfirm
            open={open}
            onOpenChange={(value) => !disabled && setOpen(value)}
            placement="topRight"
            title={next ? 'Включить?' : 'Выключить?'}
            description={`Вы уверены, что хотите ${next ? 'включить' : 'выключить'} «${what}»?`}
            okText={next ? 'Да, включить' : 'Да, выключить'}
            cancelText="Отмена"
            onConfirm={() => { setOpen(false); onConfirm(next) }}
            onCancel={() => setOpen(false)}
        >
            <Switch checked={checked} disabled={disabled} />
        </Popconfirm>
    )
}

// Настройки импорта задач, у которых есть собственное состояние ввода:
// список заголовков и режим «исполнитель не найден». Вынесены отдельно, чтобы
// не тащить черновик ввода в состояние всей страницы настроек.
function TaskImportDetails({
    skipTitles,
    unassignedMode,
    onSave,
}: {
    skipTitles: string[]
    unassignedMode: 'unassigned' | 'skip'
    onSave: (data: { pull_tasks_skip_titles?: string[]; pull_tasks_unassigned_mode?: 'unassigned' | 'skip' }) => void
}) {
    const [draft, setDraft] = useState(skipTitles.join('\n'))

    // Настройки могли поменяться в другой вкладке - подхватываем.
    useEffect(() => { setDraft(skipTitles.join('\n')) }, [skipTitles])

    return (
        <Space direction="vertical" size={8} style={{ width: '100%', paddingLeft: 44 }}>
            <Space size={12} wrap>
                <Text type="secondary" style={{ fontSize: 12 }}>Если исполнителя нет в Оркестраторе:</Text>
                <Select
                    size="small"
                    style={{ width: 240 }}
                    value={unassignedMode}
                    onChange={(value) => onSave({ pull_tasks_unassigned_mode: value })}
                    options={[
                        { value: 'unassigned', label: 'Забрать без исполнителя' },
                        { value: 'skip', label: 'Не забирать такую задачу' },
                    ]}
                />
            </Space>
            <Text type="secondary" style={{ fontSize: 12 }}>
                Заголовки автозадач их CRM — такие не забираем, по одному в строке. У них
                автозадачи создаёт их собственное правило SLA; если появится новый вид,
                добавьте его заголовок сюда.
            </Text>
            <Input.TextArea
                rows={4}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onBlur={() => {
                    const next = draft.split('\n').map((item) => item.trim()).filter(Boolean)
                    if (next.join('\n') !== skipTitles.join('\n')) {
                        onSave({ pull_tasks_skip_titles: next })
                    }
                }}
            />
        </Space>
    )
}

export default function CrmIntegrationSettings() {
    const [notify, notifyHolder] = notification.useNotification()
    const [settings, setSettings] = useState<Settings | null>(null)
    const [sources, setSources] = useState<CrmLeadSource[]>([])
    const [statusGroups, setStatusGroups] = useState<PullStatusGroup[]>([])
    // Справочник гео для дополнительного условия рассылки по источнику.
    const [geos, setGeos] = useState<GeoFolder[]>([])
    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [pulling, setPulling] = useState(false)
    const [pullLimit, setPullLimit] = useState<number>(50)
    // Итог загрузки держим на экране, пока его не закроют: в нём данные,
    // которые нужно скопировать и сверить в другой системе.
    const [pullResult, setPullResult] = useState<PullResult | null>(null)

    const load = useCallback(async () => {
        setLoading(true)
        try {
            const [settingsResponse, sourcesResponse, statusesResponse, geosResponse] = await Promise.all([
                crmIntegrationApi.getSettings(),
                crmIntegrationApi.getSources(),
                crmIntegrationApi.getPullStatuses(),
                leadsApi.getGeos(),
            ])
            setSettings(settingsResponse.data)
            setSources(sourcesResponse.data)
            setStatusGroups(statusesResponse.data.groups)
            setGeos(geosResponse.data)
            setPullLimit(settingsResponse.data.leads_limit)
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        load()
    }, [load])

    const saveSettings = async (patch: Parameters<typeof crmIntegrationApi.updateSettings>[0]) => {
        try {
            await crmIntegrationApi.updateSettings(patch)
            setSettings((current) => (current ? { ...current, ...patch } as Settings : current))
        } catch (error) {
            message.error(extractError(error))
        }
    }

    const refreshSources = async () => {
        setRefreshing(true)
        try {
            const response = await crmIntegrationApi.refreshSources()
            const added = response.data.geo_added
            message.success(
                `Источников получено: ${response.data.count}`
                + (added ? `, новых гео: ${added}` : ''),
            )
            await load()
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setRefreshing(false)
        }
    }

    // Текст итога собираем один раз: он идёт и в уведомление, и в плашку.
    const pullSummary = (data: PullResult) =>
        `Получено ${data.fetched}, добавлено ${data.created}`
        + (data.skipped_duplicate ? `, дублей ${data.skipped_duplicate}` : '')
        + (data.failed ? `, с ошибкой ${data.failed}` : '')
        + (data.assigned ? `, закреплено за сотрудником ${data.assigned}` : '')
        + (data.assignee_not_found ? `, ответственный из CRM не найден у ${data.assignee_not_found}` : '')
        + `. Осталось к загрузке: ${data.remaining}`

    const DUPLICATE_REASONS: Record<string, string> = {
        telegram_id: 'совпал TG ID',
        telegram: 'совпал телеграм',
        phone: 'совпал телефон',
        name_birth: 'совпали имя, фамилия и дата рождения',
    }

    const duplicateLine = (item: PullResult['duplicates'][number]) =>
        `ID CRM WW ${item.crm_ww_id} · ${item.title || 'без названия'} · `
        + `${item.telegram_contact || item.phone || '—'} → наш лид #${item.lead_id} ${item.lead_title || ''} · `
        + (DUPLICATE_REASONS[item.reason] || 'совпал телефон')

    const pullLeads = async () => {
        setPulling(true)
        try {
            const { data } = await crmIntegrationApi.pullLeads(pullLimit)
            setPullResult(data)
            // Уведомление висит, пока его не закроют крестиком: в нём данные,
            // которые нужно скопировать и сверить в другой системе.
            notify.open({
                type: data.duplicates.length || data.failed ? 'warning' : 'success',
                message: 'Загрузка лидов завершена',
                duration: 0,
                description: (
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        <Text>{pullSummary(data)}</Text>
                        {data.duplicates.map((item) => (
                            <Text key={item.crm_ww_id} style={{ fontSize: 12 }}>{duplicateLine(item)}</Text>
                        ))}
                    </Space>
                ),
            })
            await load()
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setPulling(false)
        }
    }

    const toggleSource = async (source: CrmLeadSource, enabled: boolean) => {
        setSources((items) => items.map((item) => (item.id === source.id ? { ...item, enabled } : item)))
        try {
            await crmIntegrationApi.updateSource(source.id, { enabled })
        } catch (error) {
            setSources((items) => items.map((item) => (item.id === source.id ? { ...item, enabled: !enabled } : item)))
            message.error(extractError(error))
        }
    }

    // Смена температуры источника переставляет и тех его лидов, которые ещё
    // ждут первого сообщения: иначе они ушли бы через старый пул.
    const changeTemperature = async (source: CrmLeadSource, temperature: SourceTemperature) => {
        const previous = source.temperature
        setSources((items) => items.map((item) => (item.id === source.id ? { ...item, temperature } : item)))
        try {
            const { data } = await crmIntegrationApi.updateSource(source.id, { temperature })
            const label = TEMPERATURE_OPTIONS.find((item) => item.value === temperature)?.label || temperature
            message.success(
                `«${source.name}» → ${label}`
                + (data.retagged ? `, переставлено лидов в очереди: ${data.retagged}` : ''),
            )
        } catch (error) {
            setSources((items) => items.map((item) => (item.id === source.id ? { ...item, temperature: previous } : item)))
            message.error(extractError(error))
        }
    }

    const toggleStatus = (code: string, checked: boolean) => {
        const current = settings?.pull_statuses || []
        const next = checked ? [...current, code] : current.filter((item) => item !== code)
        saveSettings({ pull_statuses: next })
    }

    // Отметить или снять сразу все статусы.
    const setStatuses = (codes: string[], checked: boolean) => {
        const current = new Set(settings?.pull_statuses || [])
        codes.forEach((code) => (checked ? current.add(code) : current.delete(code)))
        saveSettings({ pull_statuses: [...current] })
    }

    const allStatusCodes = useMemo(
        () => statusGroups.flatMap((group) => group.reasons.map((item) => item.code)),
        [statusGroups],
    )
    const selectedStatuses = settings?.pull_statuses || []
    const checkedCount = (codes: string[]) => codes.filter((code) => selectedStatuses.includes(code)).length

    return (
        <Spin spinning={loading}>
            {notifyHolder}
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                {settings?.configured && !settings.connected && (
                    <Alert type="error" showIcon message="База CRM Wiseway недоступна" description={settings.connection_detail} />
                )}
                {settings?.configured && settings.connected && (
                    <Alert type="success" showIcon message="Подключение к CRM Wiseway работает" />
                )}

                {settings?.last_error && (
                    <Alert type="warning" showIcon message="Последняя ошибка синхронизации" description={settings.last_error} />
                )}

                <Space size={12} align="center" wrap>
                    <span>Загрузить</span>
                    <InputNumber
                        min={1}
                        max={500}
                        value={pullLimit}
                        onChange={(value) => value && setPullLimit(Number(value))}
                        style={{ width: 100 }}
                    />
                    <span>лидов из отмеченных источников</span>
                    <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        loading={pulling}
                        disabled={!sources.some((item) => item.enabled)}
                        onClick={pullLeads}
                    >
                        Загрузить сейчас
                    </Button>
                </Space>

                {pullResult && (
                    <Alert
                        type={pullResult.duplicates.length || pullResult.failed ? 'warning' : 'success'}
                        closable
                        onClose={() => setPullResult(null)}
                        message={pullSummary(pullResult)}
                        description={pullResult.duplicates.length ? (
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Эти лиды не загружены — у нас уже есть карточка того же человека.
                                    Записи сохранены в разделе «Дубли», повторно они запрашиваться не будут.
                                </Text>
                                {pullResult.duplicates.map((item) => (
                                    <Text key={item.crm_ww_id} style={{ fontSize: 12 }}>{duplicateLine(item)}</Text>
                                ))}
                            </Space>
                        ) : undefined}
                    />
                )}

                <Text type="secondary" style={{ fontSize: 12 }}>
                    Последняя загрузка лидов: {formatMoment(settings?.last_leads_sync_at ?? null)}
                </Text>

                <Space size={24} wrap>
                    <div>
                        <div style={{ marginBottom: 4 }}>Обновлять источники раз в</div>
                        <InputNumber
                            min={5}
                            max={1440}
                            addonAfter="мин"
                            value={settings?.sources_refresh_minutes}
                            onChange={(value) => value && saveSettings({ sources_refresh_minutes: Number(value) })}
                        />
                    </div>
                    <div>
                        <div style={{ marginBottom: 4 }}>Загружать лидов раз в</div>
                        <InputNumber
                            min={5}
                            max={1440}
                            addonAfter="мин"
                            value={settings?.leads_refresh_minutes}
                            onChange={(value) => value && saveSettings({ leads_refresh_minutes: Number(value) })}
                        />
                    </div>
                    <div>
                        <div style={{ marginBottom: 4 }}>Лидов за один запрос</div>
                        <InputNumber
                            min={1}
                            max={500}
                            value={settings?.leads_limit}
                            onChange={(value) => value && saveSettings({ leads_limit: Number(value) })}
                        />
                    </div>
                </Space>

                <Space size={12} align="start">
                    <ConfirmSwitch
                        checked={settings?.auto_pull_enabled}
                        what="Загружать лидов автоматически"
                        onConfirm={(next) => saveSettings({ auto_pull_enabled: next })}
                    />
                    <div>
                        <div>Загружать лидов автоматически</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            По расписанию из включённых ниже источников. Выключено — только вручную.
                        </Text>
                    </div>
                </Space>

                <Space size={12} align="start">
                    <ConfirmSwitch
                        checked={settings?.push_assignee_enabled}
                        what="Передавать ответственного в CRM Wiseway"
                        onConfirm={(next) => saveSettings({ push_assignee_enabled: next })}
                    />
                    <div>
                        <div>Передавать ответственного в CRM Wiseway</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            При взятии лида в работу в поле «Менеджер» карточки CRM Wiseway проставляется
                            сотрудник по его почте. «Ответственный АМ за онбординг» не меняется.
                        </Text>
                    </div>
                </Space>

                {settings?.push_assignee_enabled && (
                    <Space size={12} align="start" style={{ paddingLeft: 44 }}>
                        <ConfirmSwitch
                            checked={settings?.push_assignee_overwrite}
                            what="Перезаписывать уже назначенного ответственного"
                            onConfirm={(next) => saveSettings({ push_assignee_overwrite: next })}
                        />
                        <div>
                            <div>Перезаписывать уже назначенного ответственного</div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Выключено — если в CRM Wiseway у лида уже есть другой ответственный («Менеджер», а при пустом —
                                «АМ за онбординг»), он остаётся.
                            </Text>
                        </div>
                    </Space>
                )}

                <Space size={12} align="start">
                    <ConfirmSwitch
                        checked={settings?.push_status_enabled}
                        what="Передавать статусы в CRM Wiseway"
                        onConfirm={(next) => saveSettings({ push_status_enabled: next })}
                    />
                    <div>
                        <div>Передавать статусы в CRM Wiseway</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Все записи статусов в карточку Wiseway: закрытие и переоткрытие диалога,
                            ручная смена статуса, «Первое сообщение отправлено» после рассылки, статус
                            после ответа лида и правила SLA. Лид, уже ушедший в онбординг, при закрытии
                            не трогается. Выключено — статусы меняются только в Оркестраторе.
                        </Text>
                    </div>
                </Space>

                <Space size={12} align="start">
                    <ConfirmSwitch
                        checked={settings?.push_notes_enabled}
                        what="Передавать заметки в CRM Wiseway"
                        onConfirm={(next) => saveSettings({ push_notes_enabled: next })}
                    />
                    <div>
                        <div>Передавать заметки в CRM Wiseway</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Текст заметки, написанной в карточке лида, добавляется в ленту комментариев
                            их карточки от имени автора заметки. Только добавление новой записи: ничего
                            в их базе не перезаписывается. Выключено — заметки остаются в Оркестраторе.
                        </Text>
                    </div>
                </Space>

                <Space size={12} align="start">
                    <ConfirmSwitch
                        checked={settings?.push_postpone_comment_enabled}
                        what="Передавать комментарий при откладывании"
                        onConfirm={(next) => saveSettings({ push_postpone_comment_enabled: next })}
                    />
                    <div>
                        <div>Передавать комментарий при откладывании</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Комментарий, с которым оператор откладывает диалог, добавляется в ленту
                            комментариев их карточки. Статус в воронке Wiseway при этом не меняется:
                            для них отложенный лид остаётся в работе.
                        </Text>
                    </div>
                </Space>

                <Space size={12} align="start">
                    <ConfirmSwitch
                        checked={settings?.push_action_comments_enabled}
                        what="Комментарий с именем сотрудника"
                        onConfirm={(next) => saveSettings({ push_action_comments_enabled: next })}
                    />
                    <div>
                        <div>Комментарий с именем сотрудника при смене статуса и полей</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            У смены статуса, закрытия, переоткрытия, правки полей и назначения менеджера в
                            базе Wiseway есть только «кем изменено» — пользователь по почте, а почтой профиля
                            пользуется вся команда. Включено — после каждого такого действия в ленту
                            комментариев их карточки добавляется запись вида «Иван Петров (Оркестратор):
                            статус «Агент согласен»». Автоматические действия подписываются правилом:
                            «Оркестратор (авто, правило SLA): …». Только добавление новой записи: ничего в
                            их базе не перезаписывается. В заметках, задачах и комментарии при откладывании
                            имя сотрудника стоит в тексте всегда, этот переключатель их не касается.
                        </Text>
                    </div>
                </Space>

                <Space size={12} align="start">
                    <ConfirmSwitch
                        checked={settings?.pull_tasks_enabled}
                        what="Забирать задачи из CRM Wiseway"
                        onConfirm={(next) => saveSettings({ pull_tasks_enabled: next })}
                    />
                    <div>
                        <div>Забирать задачи из CRM Wiseway</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Задачи, которые их сотрудники ставят в CRM, появляются в разделе
                            «Задачи» с меткой «CRM». Автозадачи их правил SLA не забираются —
                            список заголовков ниже. Только чтение: в их базу ничего не пишется.
                            При включении история не подтягивается, берутся только новые задачи.
                            {settings?.last_tasks_sync_at
                                ? ` Последняя проверка: ${formatMoment(settings.last_tasks_sync_at)}.`
                                : ''}
                        </Text>
                    </div>
                </Space>

                {settings?.pull_tasks_enabled && (
                    <>
                        <Space size={12} align="start">
                            <ConfirmSwitch
                                checked={settings?.pull_tasks_own_leads_only}
                                what="Забирать задачи только по нашим лидам"
                                onConfirm={(next) => saveSettings({ pull_tasks_own_leads_only: next })}
                            />
                            <div>
                                <div>Только по нашим лидам</div>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Задача приедет, только если её лид есть в Оркестраторе.
                                    Выключено — забираем и задачи по чужим лидам, и задачи без лида.
                                </Text>
                            </div>
                        </Space>
                        <Space size={12} align="start">
                            <ConfirmSwitch
                                checked={settings?.pull_tasks_by_leads}
                                what="Забирать задачи по нашим лидам"
                                onConfirm={(next) => saveSettings({ pull_tasks_by_leads: next })}
                            />
                            <div>
                                <div>Забирать и уже существующие задачи по нашим лидам</div>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Без этого приезжают только задачи, созданные после включения.
                                    С ним подбираются и поставленные раньше — но только по лидам,
                                    которые есть в Оркестраторе. Их базу целиком мы не читаем:
                                    выборка идёт по списку наших карточек.
                                </Text>
                            </div>
                        </Space>
                        <TaskImportDetails
                            skipTitles={settings?.pull_tasks_skip_titles ?? []}
                            unassignedMode={settings?.pull_tasks_unassigned_mode ?? 'unassigned'}
                            onSave={(data) => saveSettings(data as never)}
                        />
                    </>
                )}

                <Space size={12} align="start">
                    <ConfirmSwitch
                        checked={settings?.watch_tasks_enabled}
                        what="Следить за изменениями задач в CRM"
                        onConfirm={(next) => saveSettings({ watch_tasks_enabled: next })}
                    />
                    <div>
                        <div>Следить за изменениями задач в CRM</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Если связанную задачу закрыли или перенесли в их CRM — то же произойдёт
                            и у нас. Проверяются только задачи, номера которых мы уже знаем, поэтому
                            их база от этого не нагружается. Тоже только чтение.
                        </Text>
                    </div>
                </Space>

                <Space size={12} align="start">
                    <ConfirmSwitch
                        checked={settings?.push_tasks_enabled}
                        what="Передавать задачи в CRM Wiseway"
                        onConfirm={(next) => saveSettings({ push_tasks_enabled: next })}
                    />
                    <div>
                        <div>Передавать задачи в CRM Wiseway</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Задача, созданная в Оркестраторе по лиду с карточкой CRM, заводится и у
                            них, а её номер сохраняется у нас. Повторно одна и та же задача туда не
                            уедет. Внутренние задачи без лида остаются только в Оркестраторе.
                            Автозадачи наших правил SLA не передаются: они внутренние, и их бывает много.
                        </Text>
                    </div>
                </Space>

                <Space size={12} align="start">
                    <ConfirmSwitch
                        checked={settings?.close_tasks_in_crm_enabled}
                        what="Закрывать задачи в CRM Wiseway"
                        onConfirm={(next) => saveSettings({ close_tasks_in_crm_enabled: next })}
                    />
                    <div>
                        <div>Закрывать задачи в CRM Wiseway</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Закрыли связанную задачу в Оркестраторе — она закрывается и у них, а
                            комментарий попадает в их поле «Результат». Касается и задач, приехавших
                            из CRM: оператор работает здесь, и висеть открытой у них она не должна.
                            Имя закрывшего добавляется в текст — автора правки их база не хранит.
                        </Text>
                    </div>
                </Space>

                <Space size={12} align="start">
                    <ConfirmSwitch
                        checked={settings?.pull_lead_fields_enabled}
                        what="Забирать изменения карточек лидов из CRM"
                        onConfirm={(next) => saveSettings({ pull_lead_fields_enabled: next })}
                    />
                    <div>
                        <div>Забирать изменения карточек лидов из CRM</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Правки в их карточке — имя, телефон, почта, условия, ссылки, KYC —
                            приезжают к нам тем же проходом, что читает статусы. Отдельных запросов
                            «не поменялось ли что-нибудь» нет: карточка перечитывается, только если
                            у неё сдвинулась отметка изменения, которая уже есть в чтении статусов.
                            Поле, которое у них не трогали, вашу правку не затрёт.
                            Последний раз: {formatMoment(settings?.last_lead_fields_sync_at ?? null)}
                        </Text>
                    </div>
                </Space>

                <Space size={12} align="start">
                    <ConfirmSwitch
                        checked={settings?.push_lead_fields_enabled}
                        what="Передавать изменения карточки в CRM Wiseway"
                        onConfirm={(next) => saveSettings({ push_lead_fields_enabled: next })}
                    />
                    <div>
                        <div>Передавать изменения карточки в CRM Wiseway</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Сохранили карточку у нас — изменённые поля сразу уходят в их карточку,
                            одним запросом и только те, что действительно поменялись. Телеграм, статус
                            и ID карточки так не передаются: телеграм — ключ сверки, у статуса свой
                            переключатель. Если передать не удалось, правка у нас всё равно сохранится,
                            а оператор увидит предупреждение.
                        </Text>
                    </div>
                </Space>

                <div>
                    <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 4 }}>
                        <Text strong>Источник → загружать лидов</Text>
                        <Button size="small" icon={<ReloadOutlined />} loading={refreshing} onClick={refreshSources}>
                            Обновить список
                        </Button>
                    </Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Список источников синхронизируется из БД CRM WW. Обновлено: {formatMoment(settings?.last_sources_sync_at ?? null)}
                    </Text>
                </div>

                {sources.length === 0 ? (
                    <Empty description="Источники ещё не загружены" />
                ) : (
                    <div className="crm-source-list">
                        {sources.map((source) => (
                            <div className="crm-source-row" key={source.id}>
                                <span className="crm-source-name">
                                    {source.name}
                                    {/* Источник, пропавший из выгрузки, оставляем, но помечаем:
                                        иначе потерялась бы настройка включения. */}
                                    {settings?.last_sources_sync_at && source.last_seen_at
                                        && dayjs(source.last_seen_at).isBefore(dayjs(settings.last_sources_sync_at).subtract(1, 'minute')) && (
                                        <Tag color="default" style={{ marginLeft: 8 }}>нет в CRM</Tag>
                                    )}
                                </span>
                                <Space size={12} align="center">
                                    {/* Источник с температурой, под которую нет рабочих
                                        аккаунтов, встанет в очереди - предупреждаем сразу. */}
                                    {settings && (settings.pool_account_counts?.[source.temperature] ?? 0) === 0 && (
                                        <Tag color="red">нет аккаунтов в пуле</Tag>
                                    )}
                                    {/* Выбранная температура красится цветом своего пула -
                                        тем же, каким она подписана у TG-аккаунтов. */}
                                    <Segmented
                                        size="small"
                                        className="pool-segmented"
                                        style={{
                                            '--pool-color': temperatureColor(source.temperature),
                                            '--pool-text': POOL_TEXT_COLORS[source.temperature] || '#fff',
                                        } as CSSProperties}
                                        value={source.temperature}
                                        options={TEMPERATURE_OPTIONS}
                                        onChange={(value) => changeTemperature(source, value as SourceTemperature)}
                                    />
                                    <ConfirmSwitch
                                        checked={source.enabled}
                                        what={`загрузку лидов из «${source.name}»`}
                                        onConfirm={(next) => toggleSource(source, next)}
                                    />
                                </Space>
                            </div>
                        ))}
                    </div>
                )}

                <div>
                    {/* Красным - чтобы не пропустить: от этого фильтра зависит, какие лиды приедут. */}
                    <Text strong type="danger">Гео, которые загружаем</Text>
                    <div style={{ marginTop: 6 }}>
                        <Select
                            mode="multiple"
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            className="crm-geo-select"
                            size="small"
                            style={{ width: 240 }}
                            placeholder="Все гео"
                            maxTagCount="responsive"
                            value={settings?.pull_geos || []}
                            onChange={(value) => saveSettings({ pull_geos: value })}
                            options={geos.map((geo) => ({ value: geo.name, label: geo.name }))}
                            // Чекбокс вместо галочки справа: список длинный, и
                            // отмеченное должно читаться с одного взгляда.
                            menuItemSelectedIcon={null}
                            optionRender={(option) => (
                                <Checkbox checked={(settings?.pull_geos || []).includes(String(option.value))}>
                                    {option.label}
                                </Checkbox>
                            )}
                        />
                    </div>
                    <div style={{ marginTop: 6 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Условие загрузки: из CRM Wiseway забираем только лидов отмеченных гео. Ничего не
                            отмечено — «Все гео», берём как раньше. На подбор аккаунта для рассылки не влияет.
                        </Text>
                    </div>
                </div>

                <Text type="secondary" style={{ fontSize: 12 }}>
                    Температура источника определяет пул TG-аккаунтов: лид уходит только через аккаунт того же пула,
                    а если свободного аккаунта в нём нет — ждёт в очереди с этой причиной, а не уходит с чужого пула.
                    Уже загруженные и совпадающие по телефону или телеграму лиды пропускаются, повторная отправка
                    первого сообщения им не идёт. Лиды без телеграма не загружаются.
                </Text>

                <Space size={12} align="start">
                    <ConfirmSwitch
                        checked={settings?.pull_with_assignee}
                        what="Загружать лидов с ответственным"
                        onConfirm={(next) => saveSettings({ pull_with_assignee: next })}
                    />
                    <div>
                        <div>Загружать лидов с ответственным</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Берём и тех лидов, у которых в CRM Wiseway уже есть ответственный («Менеджер», а при пустом —
                            «АМ за онбординг»).
                            В Оркестраторе такой лид сразу закрепляется за тем же сотрудником — сверка идёт по
                            почте Wiseway из карточки пользователя. Совпадения нет — лид приезжает ничьим, счётчик
                            это покажет. Выключено — берём только лидов без ответственного.
                        </Text>
                    </div>
                </Space>

                <div>
                    <Space style={{ width: '100%', justifyContent: 'space-between' }} align="center">
                        <Text strong>Статусы CRM Wiseway, которые загружаем</Text>
                        <Checkbox
                            checked={allStatusCodes.length > 0 && checkedCount(allStatusCodes) === allStatusCodes.length}
                            indeterminate={checkedCount(allStatusCodes) > 0 && checkedCount(allStatusCodes) < allStatusCodes.length}
                            onChange={(event) => setStatuses(allStatusCodes, event.target.checked)}
                        >
                            Все
                        </Checkbox>
                    </Space>
                    <div style={{ marginTop: 4 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Ничего не отмечено — загрузка не идёт. По умолчанию берётся только «Новый».
                        </Text>
                    </div>
                    <div style={{ marginTop: 10, display: 'grid', gap: 12 }}>
                        {statusGroups.map((group) => (
                                <div key={group.group}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>{group.group}</Text>
                                    <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: '8px 18px' }}>
                                        {group.reasons.map((status) => (
                                            <Checkbox
                                                key={status.code}
                                                checked={selectedStatuses.includes(status.code)}
                                                onChange={(event) => toggleStatus(status.code, event.target.checked)}
                                            >
                                                {status.label}
                                            </Checkbox>
                                        ))}
                                    </div>
                                </div>
                        ))}
                    </div>
                </div>

            </Space>
        </Spin>
    )
}
